import {
  forwardRef,
  chakra,
  useMultiStyleConfig,
  useStyleConfig,
} from '@chakra-ui/react';
import type { HTMLChakraProps } from '@chakra-ui/react';
import { useMemo } from 'react';
import type { ChangeEventHandler, FocusEventHandler, ReactNode } from 'react';

import { SearchInputLeftElement } from './SearchInputLeftElement';
import { SearchInputRightElement } from './SearchInputRightElement';
import {
  type SearchInputStyles,
  SearchInputStylesProvider,
} from './SearchInputStylesContext';
import type { SearchInputMode, SearchInputSize } from './types';
import { SearchInputProvider } from './utils';

export interface SearchInputProps extends HTMLChakraProps<'div'> {
  mode?: SearchInputMode;
  size?: SearchInputSize;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isReadOnly?: boolean;
  value?: string;
  children?: ReactNode;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onClear?: () => void;
  onSubmit?: () => void;
}

export const SearchInput = forwardRef<SearchInputProps, 'div'>(
  (
    {
      mode = 'live',
      size = 'md',
      children,
      onChange,
      onFocus,
      onBlur,
      onClear,
      onSubmit,
      value,
      isDisabled,
      isInvalid,
      isReadOnly,
      ...restProps
    },
    ref,
  ) => {
    const searchInputStyles = useMultiStyleConfig('SearchInput', {
      size,
    }) as SearchInputStyles;
    const iconButtonStyles = useStyleConfig('IconButton', { size });

    // Control State for Search Input
    const isLiveMode = mode === 'live';
    const isSubmitMode = mode === 'submit';
    const isLiveEnabled = Boolean(value && value !== '' && mode === 'live');
    const isSubmitEnabled = Boolean(value && value !== '' && mode === 'submit');

    // Calculate Padding
    const iconsLeft: string[] = [];
    const iconsRight: string[] = [];
    if (mode === 'live') {
      iconsLeft.push('searchIcon');
    }
    if (mode === 'submit') {
      iconsRight.push('searchIcon');
    }
    if (value && value !== '') {
      iconsRight.push('clearIcon');
    }
    const numberOfIconsLeft = iconsLeft.length;
    const numberOfIconsRight = iconsRight.length;

    const iconWidthRaw = iconButtonStyles?.w ?? 0;
    const iconWidthNumeric =
      typeof iconWidthRaw === 'number' ? iconWidthRaw : 0;

    const fieldPaddingRaw = searchInputStyles?.field?.px ?? 0;
    const fieldPaddingNumeric =
      typeof fieldPaddingRaw === 'number' ? fieldPaddingRaw : 0;

    const getPaddingForSide = (numIconsOnSide: number): `${number}rem` =>
      `${(iconWidthNumeric * numIconsOnSide + fieldPaddingNumeric) / 2}rem`;

    const paddingLeft = getPaddingForSide(numberOfIconsLeft);
    const paddingRight = getPaddingForSide(numberOfIconsRight);

    const providerValue = useMemo(
      () => ({
        onChange,
        onFocus,
        onBlur,
        onClear,
        onSubmit,
        value,
        mode,
        isLiveMode,
        isSubmitMode,
        isSubmitEnabled,
        isLiveEnabled,
        isDisabled,
        isInvalid,
        isReadOnly,
        paddingLeft,
        paddingRight,
        size,
      }),
      [
        onChange,
        onFocus,
        onBlur,
        onClear,
        onSubmit,
        value,
        mode,
        isLiveMode,
        isSubmitMode,
        isSubmitEnabled,
        isLiveEnabled,
        isDisabled,
        isInvalid,
        isReadOnly,
        paddingLeft,
        paddingRight,
        size,
      ],
    );

    const { container: containerStyles } = searchInputStyles;

    return (
      <SearchInputProvider value={providerValue}>
        <SearchInputStylesProvider value={searchInputStyles}>
          <chakra.div ref={ref} __css={containerStyles} {...restProps}>
            <SearchInputLeftElement />
            {children}
            <SearchInputRightElement />
          </chakra.div>
        </SearchInputStylesProvider>
      </SearchInputProvider>
    );
  },
);
SearchInput.displayName = 'SearchInput';
